import { useCallback, useEffect, useMemo } from 'react';

import classNames from 'classnames';
import { NotificationBlock, Selector } from 'components/ui';

import { fetchBookingAreas } from '../../../../../api/booking';
import useLoadingState, { LoadingState } from '../../../../../hooks/useLoadingState';
import i18n from '../../../../../providers/Locale';
import useLanguage from '../../../../../providers/Locale/hooks';
import { BookingPreferredArea, BookingSettings } from '../../../../../types/booking';
import WarningBlock from '../../../../WarningBlock';
import Button from '../../../../ui/Button';
import Checkbox from '../../../../ui/Checkbox';
import InputField from '../../../../ui/InputField';
import LoadingOverlay from '../../../../ui/LoadingOverlay';
import CustomSelect, { CustomSelectOption } from '../../../../ui/Select';
import PreferredAreaItem from '../PreferredAreaItem';
import WideSelect, { WideSelectOption } from '../WideSelect';
import { BookingStateHelpers } from '../helpers';
import { FormInputsError, FormInputsValue } from '../index';
import style from '../style.module.scss';

type StepOneProps = {
	bookingSettings: BookingSettings;
	formState: FormInputsValue;
	formError: FormInputsError;
	dayOptions: WideSelectOption[];
	personCountOptions: CustomSelectOption[];
	timeOptions: WideSelectOption[] | null;
	visitDurationOptions: CustomSelectOption[];
	locationSelector: boolean;
	setLocationSelector: (locationSelector: boolean) => void;
	onInputChange: (value: string, name: string) => void;
	onContinueBtnClick: () => void;
	preferredArea: BookingPreferredArea[];
	setPreferredArea: (preferredArea: BookingPreferredArea[]) => void;
};

const StepFirst: React.FC<StepOneProps> = (props) => {
	const {
		bookingSettings,
		formState,
		formError,
		dayOptions,
		personCountOptions,
		timeOptions,
		visitDurationOptions,
		onInputChange,
		onContinueBtnClick,
		locationSelector,
		setLocationSelector,
		preferredArea,
		setPreferredArea,
	} = props;
	const minPersonsCount = bookingSettings?.deposit?.minPersonsCount;
	const { setIsFetching, isFetching, setIsIdle } = useLoadingState(LoadingState.idle);
	const { currentLanguage } = useLanguage();

	const depositAmount = useMemo(
		() =>
			BookingStateHelpers.getDepositAmountByDate(
				bookingSettings.deposit,
				formState.time,
				formState.date
			),

		[formState.date, formState.time, bookingSettings.deposit]
	);

	const getBookingAreas = useCallback(async () => {
		setIsFetching();

		const { result } = await fetchBookingAreas(currentLanguage);

		if (result) setPreferredArea(result);

		setIsIdle();
	}, [setPreferredArea, currentLanguage]);

	const handleBookingAreas = useCallback(() => {
		setLocationSelector(!locationSelector);
		if (!preferredArea.length) {
			getBookingAreas();
		}
	}, [preferredArea, locationSelector, getBookingAreas]);

	useEffect(() => {
		if (locationSelector) getBookingAreas();
	}, [currentLanguage]);

	return (
		<>
			{bookingSettings.deposit?.active && !minPersonsCount && !!depositAmount && (
				<div className={style.paidMessage}>
					<WarningBlock>
						{i18n(
							bookingSettings.deposit.perPerson
								? 'booking.paid.inform.perPerson'
								: 'booking.paid.inform',
							{
								amount: String(depositAmount / 100),
								currency: bookingSettings.deposit?.currency || '',
							},
							true,
							{ isSpan: true }
						)}
					</WarningBlock>
				</div>
			)}

			<div className={classNames(style.item, style.twoItems)}>
				<WideSelect
					label={<div className={style.label}>{i18n('bookingDate')}</div>}
					name="date"
					withDayOfWeek
					value={formState?.date}
					error={formError?.date}
					onChange={onInputChange}
					options={dayOptions}
				/>
				{timeOptions?.length ? (
					<WideSelect
						label={<div className={style.label}>{i18n('bookingTime')}</div>}
						name="time"
						value={formState?.time}
						error={formError?.time}
						onChange={onInputChange}
						options={timeOptions}
						position="right"
					/>
				) : (
					<div>
						<div className={style.label}>{i18n('bookingTime')}</div>
						<InputField name="time" value={i18n('booking_time_not_available_label')} readOnly />
					</div>
				)}
			</div>

			{!!depositAmount &&
				bookingSettings.deposit?.active &&
				minPersonsCount &&
				Number(formState.personCount) > minPersonsCount && (
					<div className={style.paidMessage}>
						<WarningBlock>
							{i18n(
								bookingSettings?.deposit?.perPerson
									? 'booking.paid.minPersonsValue.perPerson'
									: 'booking.paid.minPersonsValue',
								{
									personCount: String(minPersonsCount),
									amount: String((depositAmount as number) / 100),
									currency: bookingSettings.deposit?.currency || '',
								},
								true,
								{ isSpan: true }
							)}
						</WarningBlock>
					</div>
				)}

			<div className={classNames(style.item, style.twoItems)}>
				<Selector.Horizontal
					label={<div className={style.label}>{i18n('number_of_people')}</div>}
					name="personCount"
					value={formState?.personCount}
					error={formError?.personCount}
					onChange={onInputChange}
					options={personCountOptions}
				/>
				<CustomSelect
					label={<div className={style.label}>{i18n('visitDuration')}</div>}
					name="visitDuration"
					value={formState?.visitDuration}
					error={formError?.visitDuration}
					onChange={onInputChange}
					options={visitDurationOptions}
				/>
			</div>

			{bookingSettings.locationSelector && (
				<>
					<div className={style.row}>
						<Checkbox
							appearance="tumbler"
							checked={locationSelector}
							onChange={handleBookingAreas}
						/>
						<div className={style.label} style={{ margin: 0 }}>
							{i18n('booking.preferred.area')}
						</div>
					</div>
					{isFetching && <LoadingOverlay />}
					{locationSelector && !!preferredArea.length && (
						<div className={style.PreferredAreaWrapper}>
							{preferredArea.map((item) => {
								return (
									<PreferredAreaItem
										name={item.name}
										id={item._id}
										isActive={formState.preferArea === item._id}
										key={item._id}
										onClick={onInputChange}
									/>
								);
							})}
						</div>
					)}
				</>
			)}

			<NotificationBlock className={style.notification}>
				{i18n('we_will_confirm_your_reservation')}
			</NotificationBlock>
			{bookingSettings.customerMessage && (
				<div className={style.message}>{bookingSettings.customerMessage}</div>
			)}
			<div className={style.nextWrapper}>
				<Button
					fullWidth
					size={'large'}
					appearance={'primary'}
					type={'submit'}
					onClick={onContinueBtnClick}>
					{i18n('next')}
				</Button>
			</div>
		</>
	);
};

export default StepFirst;
